import React, { useState, useEffect, useCallback } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';

interface ClientNotesGlobalDashboardProps {
  product: PMT2Product | null;
}

interface GlobalNoteEntry {
  id: string;
  date: Timestamp;
  name: string;
  dataElement: string;
  newValue: string;
  notes: string;
  daysAgo?: number;
  [key: string]: any;
}

/**
 * Client Notes Global Dashboard Component
 * 
 * Displays global client notes that apply to the entire company
 * Each entry shows Days Ago, Date, Name, and Notes as bullet points
 */
const ClientNotesGlobalDashboard: React.FC<ClientNotesGlobalDashboardProps> = ({ product }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [noteEntries, setNoteEntries] = useState<GlobalNoteEntry[]>([]);

  // Memoize the fetch function to avoid recreating it on every render
  const fetchGlobalNotes = useCallback(async (companyName: string) => {
    // Reset state
    setNoteEntries([]);
    setError(null);
    setLoading(true);
    
    try {
      const entries = await pmt2Service.getClientNotesGlobal(companyName);
      
      // Only update state if the product hasn't changed during the fetch
      if (product && product.companyName === companyName) {
        setNoteEntries(entries);
      }
    } catch (err) {
      setError('Failed to load global notes. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [product]);

  // Effect to fetch data when product changes
  useEffect(() => {
    // Reset state when product changes
    setNoteEntries([]);
    setError(null);
    
    if (product) {
      fetchGlobalNotes(product.companyName);
    }
  }, [product, fetchGlobalNotes]);

  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Client Notes - Global</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading global notes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Client Notes - Global</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  // Sort entries by date (newest first)
  const sortedEntries = [...noteEntries].sort((a, b) => {
    const dateA = a.date?.toDate?.() || new Date();
    const dateB = b.date?.toDate?.() || new Date();
    return dateB.getTime() - dateA.getTime();
  });

  // Calculate days ago from timestamp
  const calculateDaysAgo = (timestamp: Timestamp): number => {
    if (!timestamp) return 0;
    
    try {
      const entryDate = timestamp.toDate();
      entryDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Calculate the difference in days
      const diffTime = today.getTime() - entryDate.getTime();
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 0;
    }
  };

  // Format date to "Month Day, Year" format
  const formatDate = (timestamp: Timestamp): string => {
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = timestamp.toDate();
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (error) {
      return 'Unknown date';
    }
  };

  // Format notes as bullet points and hyperlink URLs
  const formatNotes = (text: string): string[] => {
    if (!text) return [];

    // Trim the text
    let trimmedText = text.trim();
    
    // Protect URLs from being split
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls: string[] = [];
    const textWithoutUrls = trimmedText.replace(urlRegex, (match) => {
      urls.push(match);
      return `__URL_${urls.length - 1}__`;
    });

    // Protect month ranges from being split
    const monthRangeRegex = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g;
    const textWithProtectedPatterns = textWithoutUrls.replace(monthRangeRegex, (match) => {
      return match.replace('-', '__MONTH_DASH__');
    });

    // Function to restore protected content
    const restoreProtectedContent = (text: string): string => {
      let result = text.replace(/__MONTH_DASH__/g, '-');
      result = result.replace(/__URL_(\d+)__/g, (_, index) => urls[parseInt(index)]);
      return result;
    };

    // Check if the text starts with "- " (dash followed by space)
    const startsWithDash = trimmedText.startsWith('- ');
    
    // NEW APPROACH: Handle both "- " and " - " formats
    let result: string[] = [];

    // Case 1: If the text starts with "- " (dash followed by space)
    if (startsWithDash) {
      // Split by "- " (dash followed by space) at the beginning of a line
      const parts = textWithProtectedPatterns.split(/(?:\r?\n|\r)?- /);
      
      // Process the first part (which might be empty if the text starts with "- ")
      if (parts[0].trim() !== '') {
        // Remove the leading "- " from the first part
        const firstPart = parts[0].startsWith('- ') ? parts[0].substring(2).trim() : parts[0].trim();
        if (firstPart) {
          result.push(restoreProtectedContent(firstPart));
        }
      }
      
      // Process the rest of the parts
      for (let i = 1; i < parts.length; i++) {
        const part = parts[i].trim();
        if (part) {
          result.push(restoreProtectedContent(part));
        }
      }
    } 
    // Case 2: If the text contains " - " (space, dash, space)
    else {
      // Split by " - " (space, dash, space)
      const parts = textWithProtectedPatterns.split(' - ');
      
      // Process the parts
      result = parts
        .map(part => part.trim())
        .filter(part => part.length > 0)
        .map(part => restoreProtectedContent(part));
      
      // If there's only one part and it starts with a dash, remove the dash
      if (result.length === 1 && result[0].startsWith('-')) {
        result[0] = result[0].substring(1).trim();
      } 
      // If there are multiple parts and the first part starts with a dash, remove the dash
      else if (result.length > 1 && result[0].startsWith('-')) {
        result[0] = result[0].substring(1).trim();
      }
    }
    
    return result;
  };

  // Function to render text with hyperlinked URLs
  const renderWithLinks = (text: string): React.ReactNode => {
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Split the text by URLs
    const parts = text.split(urlRegex);
    
    // Map each part to either plain text or a hyperlink
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{part}</a>;
      }
      return part;
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">Client Notes - Global</h2>
      
      {sortedEntries.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No global notes found for this company.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedEntries.map((entry, index) => {
            const daysAgo = calculateDaysAgo(entry.date); // Always calculate from timestamp
            const notes = formatNotes(entry.newValue || '');
            const formattedDate = formatDate(entry.date);
            
            return (
              <div key={`entry_${index}_${entry.id || Math.random().toString(36).substr(2, 9)}`} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-700">
                <div className="flex flex-wrap gap-4 items-center mb-2 text-sm">
                  <span className="font-medium">{daysAgo} days ago</span>
                  <span className="text-gray-500 dark:text-gray-400">{formattedDate}</span>
                  <span className="text-gray-500 dark:text-gray-400">{entry.name || 'Unknown'}</span>
                </div>
                
                {notes.length > 0 ? (
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                    {notes.map((note, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300">{renderWithLinks(note)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic mt-2 text-sm">No notes provided</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientNotesGlobalDashboard;